import {
  empty,
  emptyProjectWorkOrder,
  getProjectWorkOrder,
  getValueFromSelect,
} from "./timesUtils";
import { SelectItemNumber } from "../../SharedModule/types/shared-module-types";
import { TimeEntryForm, TimeEntrySheetView } from "../types/times-module-types";
import { isBetweenTwoDates, getDateFromISOFormat } from "../../SharedModule/utils/dateUtils";
import { decodeURIComponentSafe } from "../../SharedModule/utils/formatters";
import {
  getIndexFromMealBreakHours,
  mealBreakHours,
} from "./mealBreakHoursUtils";
import {
  BAMBOO_HR,
  billableIds,
  CHANGE_GEAR,
  CLIENT_PRICE_SCHEDULED_ERROR,
  CLOUDFARE_ERROR,
  CLOUDFARE_ERROR_MESSAGE,
  PROJ_MEAL_BREAK_ID,
  PROJ_NURSING_MOTHER_ID,
  PROJ_PAID_TIME_OFF_ID,
  PROJ_UNPAID_TIME_OFF_ID,
  STATUS_ERROR_FORBIDDEN,
  TASKTYPE_HOLIDAY_ID,
  TASKTYPE_MEAL_BREAK_ID,
  TASKTYPE_NURSING_MOTHER_ID,
  TASKTYPE_UNPAID_TIME_OFF_ID,
} from "../../SharedModule/utils/constants";

// ---------------------------------------------------------------------------
// Validity computations extracted from EntryHoursBox's handleChange, one per
// simple switch-case (ASUITE11-4390, S3776). Each function is a 1:1
// translation of the inline logic it replaces — same expression, only
// parametrized so it no longer depends on component closure/state setters.
// ---------------------------------------------------------------------------

export type EntryValidityState = {
  entryDateValid: boolean;
  entryProjectWorkOrderValid: boolean;
  entryHoursValid: boolean;
  entryTaskTypeValid: boolean;
  entryIsBillable: boolean;
  entryNonBillableReasonValid: boolean;
};

export const computeEntryDescriptionValidity = (
  entryState: Pick<
    EntryValidityState,
    | "entryDateValid"
    | "entryProjectWorkOrderValid"
    | "entryHoursValid"
    | "entryTaskTypeValid"
    | "entryIsBillable"
    | "entryNonBillableReasonValid"
  >,
  checkFutureMealOrNursing: boolean,
): boolean =>
  entryState.entryDateValid &&
  entryState.entryProjectWorkOrderValid &&
  entryState.entryHoursValid &&
  entryState.entryTaskTypeValid &&
  (entryState.entryIsBillable ||
    (!entryState.entryIsBillable && entryState.entryNonBillableReasonValid)) &&
  !checkFutureMealOrNursing;

export const computeEntryNonBillableReasonValidity = (
  entryState: Pick<
    EntryValidityState,
    | "entryDateValid"
    | "entryProjectWorkOrderValid"
    | "entryHoursValid"
    | "entryTaskTypeValid"
    | "entryIsBillable"
  >,
  value: SelectItemNumber,
  checkFutureMealOrNursing: boolean,
): { isFieldValid: boolean; actualEntryValid: boolean } => {
  const isFieldValid =
    entryState.entryIsBillable ||
    (value.label !== empty.label && value.value !== empty.value);
  const actualEntryValid =
    entryState.entryDateValid &&
    entryState.entryProjectWorkOrderValid &&
    entryState.entryHoursValid &&
    entryState.entryTaskTypeValid &&
    (entryState.entryIsBillable || isFieldValid) &&
    !checkFutureMealOrNursing;
  return { isFieldValid, actualEntryValid };
};

export const computeEntryIsBillableValidity = (
  entryState: Pick<
    EntryValidityState,
    | "entryDateValid"
    | "entryProjectWorkOrderValid"
    | "entryHoursValid"
    | "entryTaskTypeValid"
  > & { entryNonBillableReason: SelectItemNumber },
  value: boolean,
  checkFutureMealOrNursing: boolean,
): { actualEntryValid: boolean; entryNonBillableReasonValid: boolean } => {
  const reasonSet =
    entryState.entryNonBillableReason.label !== empty.label &&
    entryState.entryNonBillableReason.value !== empty.value;
  const actualEntryValid =
    entryState.entryDateValid &&
    entryState.entryProjectWorkOrderValid &&
    entryState.entryHoursValid &&
    entryState.entryTaskTypeValid &&
    (value || reasonSet) &&
    !checkFutureMealOrNursing;
  return {
    actualEntryValid,
    entryNonBillableReasonValid: value ? value : reasonSet,
  };
};

// ---------------------------------------------------------------------------
// Shared "restricted future meal-break/nursing-mother edit" predicate
// (ASUITE11-2507). handleChange repeats this check, with only the
// "is this a meal-break/nursing context" condition varying, at five call
// sites: the top-of-function checkFutureMealOrNursing, checkFutureMealBreak
// and checkFutureNursing (both in the entryProjectWorkOrder case),
// checkFutureMealOrNursingOnDate (entryDate case) and checkMealOrNursing
// (entryTaskType case).
// ---------------------------------------------------------------------------

export const isRestrictedFutureMealOrNursingEdit = (
  entryDate: string | Date,
  isMealOrNursingContext: boolean,
  today: Date,
  isSuperTimeAdmin: boolean,
  isAnyOfSupervisorPoDelivery: boolean,
  activeUserId: any,
  targetUserId: any,
): boolean =>
  !isBetweenTwoDates(entryDate, null, today) &&
  isMealOrNursingContext &&
  ((isSuperTimeAdmin && activeUserId !== targetUserId) ||
    (isAnyOfSupervisorPoDelivery && activeUserId !== targetUserId));

// ---------------------------------------------------------------------------
// Hours validity formula shared by checkHoursValid's call sites (entryHours,
// entryTaskType, entryDate, entryProjectWorkOrder cases).
// ---------------------------------------------------------------------------

export type HoursValidityOptions = {
  isMealBreak: boolean;
  editMode: boolean;
  totalHoursActualDay: number;
  initialEntryHours: number;
};

export const computeHoursValid = (
  value: any,
  project: SelectItemNumber | null | undefined,
  task: SelectItemNumber | null | undefined,
  options: HoursValidityOptions,
): boolean => {
  const isMealBreakFromProject =
    project?.value === PROJ_UNPAID_TIME_OFF_ID &&
    task?.value === TASKTYPE_MEAL_BREAK_ID;
  return options.isMealBreak || isMealBreakFromProject
    ? Number(value) > 0 && Number(value) < 1.25
    : Number(value) > 0 &&
        Number(value) < 24 &&
        Number(value) % 0.25 === 0 &&
        (options.editMode
          ? Number(value) + options.totalHoursActualDay - options.initialEntryHours <=
            24
          : Number(value) + options.totalHoursActualDay <= 24);
};

export const computeEntryHoursValidity = (
  entryState: Pick<
    EntryValidityState,
    | "entryDateValid"
    | "entryProjectWorkOrderValid"
    | "entryTaskTypeValid"
    | "entryIsBillable"
    | "entryNonBillableReasonValid"
  > & {
    entryProjectWorkOrder: SelectItemNumber;
    entryTaskType: SelectItemNumber;
  },
  value: any,
  hoursOptions: HoursValidityOptions,
  checkFutureMealOrNursing: boolean,
): { isFieldValid: boolean; actualEntryValid: boolean } => {
  const isFieldValid = computeHoursValid(
    value,
    entryState.entryProjectWorkOrder,
    entryState.entryTaskType,
    hoursOptions,
  );
  const actualEntryValid =
    entryState.entryDateValid &&
    entryState.entryProjectWorkOrderValid &&
    isFieldValid &&
    entryState.entryTaskTypeValid &&
    (entryState.entryIsBillable ||
      (!entryState.entryIsBillable && entryState.entryNonBillableReasonValid)) &&
    !checkFutureMealOrNursing;
  return { isFieldValid, actualEntryValid };
};

export type NonBillableReasonOverrides = {
  nursingMother: SelectItemNumber;
  unpaidOrMealBreak: SelectItemNumber;
};

export const computeEntryTaskTypeValidity = (
  entryState: Pick<
    EntryValidityState,
    | "entryDateValid"
    | "entryProjectWorkOrderValid"
    | "entryIsBillable"
    | "entryNonBillableReasonValid"
    | "entryHoursValid"
  > & {
    entryHours: any;
    entryProjectWorkOrder: SelectItemNumber;
    entryNonBillableReason: SelectItemNumber;
  },
  value: SelectItemNumber,
  hoursOptions: HoursValidityOptions,
  checkMealOrNursing: boolean,
  nonBillableReasonOverrides: NonBillableReasonOverrides,
): {
  isFieldValid: boolean;
  actualhoursEntryValid: boolean;
  actualEntryValid: boolean;
  isEntryValid: boolean;
  entryNonBillableReason: SelectItemNumber;
  entryNonBillableReasonValid: boolean;
  entryHoursValid: boolean;
} => {
  const isFieldValid = value.label !== empty.label && value.value !== empty.value;

  // Nursing Mother: 31, Unpaid Time Off: 42, Meal Break: 30
  const partialEntryNonBillableReason =
    value.value === TASKTYPE_NURSING_MOTHER_ID
      ? nonBillableReasonOverrides.nursingMother
      : value.value === TASKTYPE_UNPAID_TIME_OFF_ID ||
          value.value === TASKTYPE_MEAL_BREAK_ID
        ? nonBillableReasonOverrides.unpaidOrMealBreak
        : null;

  // if change to meal break, should add hours validation
  const actualhoursEntryValid = computeHoursValid(
    entryState.entryHours,
    entryState.entryProjectWorkOrder,
    value,
    hoursOptions,
  );

  const actualEntryValid =
    entryState.entryDateValid &&
    entryState.entryProjectWorkOrderValid &&
    // checkStatus's original "shouldEvalHours ? shouldEvalHours : entryHoursValid"
    // ternary — preserved as-is (characterization, not a fix).
    (actualhoursEntryValid || entryState.entryHoursValid) &&
    isFieldValid &&
    (entryState.entryIsBillable ||
      (!entryState.entryIsBillable && entryState.entryNonBillableReasonValid)) &&
    !checkMealOrNursing;

  const isMealBreakTaskType = value.value === TASKTYPE_MEAL_BREAK_ID;

  return {
    isFieldValid,
    actualhoursEntryValid,
    actualEntryValid,
    isEntryValid: isMealBreakTaskType
      ? actualEntryValid && actualhoursEntryValid
      : actualEntryValid,
    entryNonBillableReason: partialEntryNonBillableReason
      ? partialEntryNonBillableReason
      : entryState.entryNonBillableReason,
    entryNonBillableReasonValid: partialEntryNonBillableReason
      ? true
      : !!entryState.entryNonBillableReason &&
        entryState.entryNonBillableReason.label !== empty.label &&
        entryState.entryNonBillableReason.value !== empty.value,
    entryHoursValid: isMealBreakTaskType
      ? actualhoursEntryValid
      : entryState.entryHoursValid,
  };
};

// ---------------------------------------------------------------------------
// computeEntryDateValidity — the full entryDate case. Also deduplicates a
// checkHoursValid call that used to run twice with identical arguments (once
// inside checkStatus's entryDate branch, once again in handleChange's body).
// ---------------------------------------------------------------------------

export const computeEntryDateValidity = (
  entryState: Pick<
    EntryValidityState,
    | "entryProjectWorkOrderValid"
    | "entryTaskTypeValid"
    | "entryIsBillable"
    | "entryNonBillableReasonValid"
  > & {
    entryHours: any;
    entryProjectWorkOrder: SelectItemNumber;
    entryTaskType: SelectItemNumber;
  },
  isFieldValid: boolean,
  hoursOptions: HoursValidityOptions,
  existMealBreak: boolean,
  checkFutureMealOrNursingOnDate: boolean,
): { actualhoursEntryValid: boolean; actualEntryValid: boolean } => {
  const actualhoursEntryValid = computeHoursValid(
    entryState.entryHours,
    entryState.entryProjectWorkOrder,
    entryState.entryTaskType,
    hoursOptions,
  );
  const actualEntryValid =
    isFieldValid &&
    entryState.entryProjectWorkOrderValid &&
    actualhoursEntryValid &&
    entryState.entryTaskTypeValid &&
    (entryState.entryIsBillable ||
      (!entryState.entryIsBillable && entryState.entryNonBillableReasonValid)) &&
    !existMealBreak &&
    !checkFutureMealOrNursingOnDate;
  return { actualhoursEntryValid, actualEntryValid };
};

// ---------------------------------------------------------------------------
// entryProjectWorkOrder case — split across its two branches (meal break vs.
// everything else). The side-effecting parts (setState calls, the
// updateMealBreakHours call and its mutation of the render-scoped
// partialDescription/mealBreakState) stay in the component; only the
// validity booleans and the resulting state-patch object are extracted.
// ---------------------------------------------------------------------------

export const computeMealBreakProjectEntryValid = (
  isFieldValid: boolean,
  entryDateValid: boolean,
  existMealBreak: boolean,
  checkFutureMealBreak: boolean,
  entryHoursValid: boolean,
): boolean => {
  // firstTimeMealBreakHoursValid is always true on this branch (set
  // unconditionally right before this check in the original code) —
  // preserved as an explicit literal rather than simplifying the ternary.
  const firstTimeMealBreakHoursValid = true;
  return isFieldValid &&
    entryDateValid &&
    !existMealBreak &&
    !checkFutureMealBreak &&
    firstTimeMealBreakHoursValid
    ? firstTimeMealBreakHoursValid
    : entryHoursValid;
};

export const computeNonMealBreakProjectEntryValid = (
  entryState: Pick<
    EntryValidityState,
    | "entryDateValid"
    | "entryTaskTypeValid"
    | "entryIsBillable"
    | "entryNonBillableReasonValid"
    | "entryHoursValid"
  >,
  value: SelectItemNumber & { defaultTaskTypeId?: number },
  isFieldValid: boolean,
  actualhoursEntryValid: boolean,
  previousMealBreakState: boolean,
  isNursingMother: boolean,
  checkFutureNursing: boolean,
): boolean => {
  if (previousMealBreakState) return false;
  if (isNursingMother) {
    return (
      entryState.entryDateValid &&
      isFieldValid &&
      actualhoursEntryValid &&
      !checkFutureNursing
    );
  }
  // checkStatus's original "entryProjectWorkOrder" case, inlined. The
  // "shouldEvalHours ? shouldEvalHours : entryHoursValid" ternary is
  // preserved as-is (characterization, not a fix).
  return (
    entryState.entryDateValid &&
    isFieldValid &&
    (actualhoursEntryValid || entryState.entryHoursValid) &&
    (entryState.entryTaskTypeValid || !!value.defaultTaskTypeId) &&
    (entryState.entryIsBillable ||
      (!entryState.entryIsBillable && entryState.entryNonBillableReasonValid)) &&
    !!value.defaultTaskTypeId
  );
};

export type NonMealBreakProjectMetaSelectors = {
  nonBillableReasons: SelectItemNumber[];
  taskTypes: SelectItemNumber[];
  nursingMotherReason: SelectItemNumber;
  internalReason: SelectItemNumber;
  nursingMotherTaskType: SelectItemNumber;
};

export const computeNonMealBreakProjectPatch = (
  entryState: { entryDescription: string; entryHours: any },
  value: SelectItemNumber & {
    entityId?: number;
    isInternal?: boolean;
    defaultTaskTypeId?: number;
  },
  actualhoursEntryValid: boolean,
  previousMealBreakState: boolean,
  metaSelectors: NonMealBreakProjectMetaSelectors,
) => ({
  entryIsBillable: value.isInternal ? false : true,
  entryNonBillableReason:
    value.value === PROJ_NURSING_MOTHER_ID
      ? metaSelectors.nursingMotherReason
      : value.entityId === PROJ_PAID_TIME_OFF_ID ||
          value.entityId === PROJ_UNPAID_TIME_OFF_ID
        ? getValueFromSelect(
            metaSelectors.nonBillableReasons,
            value.entityId === PROJ_PAID_TIME_OFF_ID ? 8 : 9,
          )
        : value.isInternal
          ? metaSelectors.internalReason
          : empty,
  entryNonBillableReasonValid: value.isInternal,
  entryTaskType:
    value.value === PROJ_NURSING_MOTHER_ID
      ? metaSelectors.nursingMotherTaskType
      : !!value.defaultTaskTypeId
        ? getValueFromSelect(metaSelectors.taskTypes, value.defaultTaskTypeId)
        : empty,
  entryTaskTypeValid:
    value.value === PROJ_NURSING_MOTHER_ID ? true : !!value.defaultTaskTypeId,
  entryDescription: previousMealBreakState ? "" : entryState.entryDescription,
  entryHours: previousMealBreakState ? "" : entryState.entryHours,
  entryHoursValid: previousMealBreakState ? false : actualhoursEntryValid,
});

// ---------------------------------------------------------------------------
// parseEditSaveError — saveAction's edit-mode .catch callback (ASUITE11-4412,
// S3776). A 1:1 translation of the original error-classification logic; the
// dispatch/props calls (toast, showClientPriceLevelError, reloadPage,
// showBackendEntryError) stay in the component, only the decision of what to
// show is extracted.
// ---------------------------------------------------------------------------

export type EditSaveErrorResult = {
  isClientPriceLevelError: boolean;
  toastMessage: string;
  backendBannerMessage: string;
};

export const parseEditSaveError = (
  err: any,
  canShowBanner: boolean,
): EditSaveErrorResult => {
  // ASUITE11-4337: the edit endpoint answers 400 with a different shape than
  // the add endpoint — no `message`, and `errors` is an array like
  // ["An error occurred trying to perform operation /api/...",
  //  "The Time Entry Date is not valid", "Trace ID: ..."]. Guard the shape
  // (a missing `error` used to throw and hang the page) and drop the generic
  // wrapper + trace lines to keep the meaningful reason.
  const data = err?.response?.data;
  const genericErrorPrefixes = [
    "An error occurred trying to perform operation",
    "An error ocurred trying to perform operation",
    "Trace ID",
  ];
  const meaningfulErrors: string[] = (
    Array.isArray(data?.errors) ? data.errors : []
  ).filter(
    (m: string) => m && !genericErrorPrefixes.some((p) => m.startsWith(p)),
  );
  const rawError: string =
    (typeof data === "string" ? "" : data?.error) || meaningfulErrors[0] || "";
  const isCloudflare =
    err?.response?.status === STATUS_ERROR_FORBIDDEN &&
    typeof data === "string" &&
    data.includes(CLOUDFARE_ERROR);

  if (rawError.startsWith(CLIENT_PRICE_SCHEDULED_ERROR)) {
    return {
      isClientPriceLevelError: true,
      toastMessage: "Time Entry was not saved correctly",
      backendBannerMessage: "",
    };
  }

  // ASUITE11-4337: parity with the list-view flow — when the page can show
  // the banner, keep the toast generic and send the specific reason to the
  // banner; otherwise keep it in the toast.
  const toastMessage = isCloudflare
    ? CLOUDFARE_ERROR_MESSAGE
    : canShowBanner
      ? data?.message || "Time Entry was not saved correctly"
      : rawError || "An error ocurred trying to perform operation";
  const backendBannerMessage =
    rawError && !isCloudflare && canShowBanner ? rawError : "";

  return {
    isClientPriceLevelError: false,
    toastMessage,
    backendBannerMessage,
  };
};

// ---------------------------------------------------------------------------
// parseAddSaveResponse — saveAction's add-mode .then callback (ASUITE11-4412,
// S3776). A 1:1 translation of the original success/error classification
// logic; the dispatch/props calls (toast, showClientPriceLevelError,
// resetPage, reloadPage, showBackendEntryError) and the outer avoidResetPage
// assignment stay in the component.
// ---------------------------------------------------------------------------

export type AddSaveResponseResult = {
  toastMode: "success" | "error";
  toastMessage: string;
  isClientPriceLevelError: boolean;
  backendBannerMessage: string;
};

export const parseAddSaveResponse = (
  response: any,
  canShowBanner: boolean,
): AddSaveResponseResult => {
  // Entry was not save correctly
  if (response.data.data && response.data.data.length > 0) {
    // ASUITE11-2700: check type of error
    const entryError: string = response.data.data[0].error ?? "";
    if (entryError.startsWith(CLIENT_PRICE_SCHEDULED_ERROR)) {
      return {
        toastMode: "error",
        toastMessage: response.data.message,
        isClientPriceLevelError: true,
        backendBannerMessage: "",
      };
    }
    // ASUITE11-4337: parity with the list-view "Save all entries" flow —
    // when the page can show the banner, the toast carries the generic
    // envelope message and the specific reason goes to the banner;
    // otherwise (Approve/Query pages) keep it in the toast.
    const toastMessage = canShowBanner
      ? response.data.message || "Time Entry was not saved correctly"
      : entryError || "Time Entry was not saved correctly";
    return {
      toastMode: "error",
      toastMessage,
      isClientPriceLevelError: false,
      backendBannerMessage: entryError && canShowBanner ? entryError : "",
    };
  }

  return {
    toastMode: "success",
    toastMessage: response.data.message,
    isClientPriceLevelError: false,
    backendBannerMessage: "",
  };
};

// ---------------------------------------------------------------------------
// computeImportedFromChangeGearOrBambooFlags — updateInfoModal's first four
// setState calls (ASUITE11-4412, S3776).
//
// wasImportedFromChangeGearOrBamboo intentionally carries the *pre-update*
// isImportedFromChangeGearOrBamboo value — the original reads that state
// variable for isImportedFromChangeGearOrBambooText right after calling
// setIsImportedFromChangeGearOrBamboo(...), and since setState doesn't apply
// synchronously, it's reading the stale, not-yet-updated value. Preserved as
// a characterization, not a fix.
// ---------------------------------------------------------------------------

export type ImportedFromChangeGearOrBambooFlags = {
  isImportedFromChangeGearOrBamboo: boolean;
  isImportedFromChangeGearOrBambooText: string;
  deletableFromBambooSuperAdmin: boolean;
  isHolidayFromBamboo: boolean;
};

export const computeImportedFromChangeGearOrBambooFlags = (
  entry: Pick<TimeEntrySheetView, "timeEntrySourceId" | "taskTypeId" | "source">,
  isSuperTimeAdmin: boolean,
  activeUserId: any,
  userIdParam: any,
  wasImportedFromChangeGearOrBamboo: boolean,
): ImportedFromChangeGearOrBambooFlags => {
  // ASUITE11-2561: imported from changeGear or Bamboo and could not edit holidays
  const isImportedFromChangeGearOrBamboo =
    entry.timeEntrySourceId === 3 ||
    (entry.timeEntrySourceId === 2 &&
      entry.taskTypeId !== TASKTYPE_HOLIDAY_ID) ||
    (entry.timeEntrySourceId === 2 &&
      entry.taskTypeId === TASKTYPE_HOLIDAY_ID &&
      !isSuperTimeAdmin) ||
    (entry.timeEntrySourceId === 2 &&
      entry.taskTypeId === TASKTYPE_HOLIDAY_ID &&
      isSuperTimeAdmin &&
      activeUserId === userIdParam);

  const isImportedFromChangeGearOrBambooText =
    wasImportedFromChangeGearOrBamboo && entry.timeEntrySourceId === 3
      ? CHANGE_GEAR
      : BAMBOO_HR;

  // ASUITE11-2736: Time Admin: Delete imported time (from bamboo whan task type is PTO or UTO) for others
  const deletableFromBambooSuperAdmin =
    entry.timeEntrySourceId === 2 &&
    isSuperTimeAdmin &&
    entry.taskTypeId !== TASKTYPE_HOLIDAY_ID;

  // should check if holiday from bamboo when change time entry in multiple on a day
  const isHolidayFromBamboo =
    entry.timeEntrySourceId === 2 &&
    entry.source === BAMBOO_HR &&
    entry.taskTypeId === TASKTYPE_HOLIDAY_ID;

  return {
    isImportedFromChangeGearOrBamboo,
    isImportedFromChangeGearOrBambooText,
    deletableFromBambooSuperAdmin,
    isHolidayFromBamboo,
  };
};

// ---------------------------------------------------------------------------
// computeMealBreakStateFromEntry — updateInfoModal's conditional
// setMealBreakState call (ASUITE11-4412, S3776).
// ---------------------------------------------------------------------------

export const computeMealBreakStateFromEntry = (
  entry: Pick<TimeEntrySheetView, "entityId" | "description">,
): { start: any; end: any } | null =>
  entry.entityId === PROJ_MEAL_BREAK_ID
    ? {
        start:
          mealBreakHours[getIndexFromMealBreakHours(entry.description, true)],
        end: mealBreakHours[
          getIndexFromMealBreakHours(entry.description, false)
        ],
      }
    : null;

// ---------------------------------------------------------------------------
// computeEditFormFromEntry — updateInfoModal's setEntryState call
// (ASUITE11-4412, S3776). The bulk of the function's original complexity:
// nested ternaries choosing entryCanEdit/entryCanDelete/
// entryProjectWorkOrder/entryTaskType/entryNonBillableReason. A 1:1
// translation of the original logic.
// ---------------------------------------------------------------------------

export type EditFormMetaSelectors = {
  projectsWorkOrders: any;
  partialInactiveEntryProjectWorkOrder: any;
  taskTypes: any[];
  nonBillableReasons: any[];
};

export const computeEditFormFromEntry = (
  entry: TimeEntrySheetView,
  isSuperTimeAdmin: boolean,
  metaSelectors: EditFormMetaSelectors,
): TimeEntryForm => {
  const actualProjectsWorkOrder = getProjectWorkOrder(
    metaSelectors.projectsWorkOrders,
    entry.entityId,
  );

  return {
    // should combine entry canEdit with status pending (timeEntryStatusId === 1 or super admin and timeEntryStatusId === 2) to allow to edit
    entryCanEdit:
      entry.timeEntrySourceId === 3 || entry.timeEntryStatusId === 3 // imported from change gear or time entry status LOCKED
        ? false
        : (entry.canEdit && entry.timeEntryStatusId === 1) ||
          (entry.timeEntryStatusId === 2 && isSuperTimeAdmin),
    entryCanDelete:
      entry.timeEntrySourceId === 3 || entry.timeEntryStatusId === 3 // imported from change gear
        ? false
        : entry.canDelete,
    entryId: entry.timeEntryId,
    entryDate: getDateFromISOFormat(entry.entryDate),
    entryDateValid: true,
    entryProjectWorkOrder: actualProjectsWorkOrder
      ? actualProjectsWorkOrder
      : metaSelectors.partialInactiveEntryProjectWorkOrder
        ? metaSelectors.partialInactiveEntryProjectWorkOrder
        : emptyProjectWorkOrder,
    entryProjectWorkOrderValid: actualProjectsWorkOrder ? true : false,
    entryHours: entry.hours,
    entryHoursValid: true,
    entryTaskType: getValueFromSelect(metaSelectors.taskTypes, entry.taskTypeId)
      ? getValueFromSelect(metaSelectors.taskTypes, entry.taskTypeId)
      : {
          value: entry.taskTypeId,
          label: entry.taskType,
        },
    entryTaskTypeValid: true,
    entryDescription: decodeURIComponentSafe(entry.description),
    entryIsBillable: billableIds.includes(
      entry.timeEntryTypeId.toString().toLowerCase(),
    ),
    entryNonBillableReason: billableIds.includes(
      entry.timeEntryTypeId.toString().toLowerCase(),
    )
      ? empty
      : getValueFromSelect(
            metaSelectors.nonBillableReasons,
            entry.nonBillableReasonId,
          )
        ? getValueFromSelect(
            metaSelectors.nonBillableReasons,
            entry.nonBillableReasonId,
          )
        : {
            value: entry.nonBillableReasonId,
            label: entry.nonBillableReason,
          },
    entryNonBillableReasonValid: entry.nonBillableReasonId ? true : false,
    isEntryValid: false,
    isEntryTouched: false,
  };
};

// ---------------------------------------------------------------------------
// computeSaveDisabled — setSaveDisabled's boolean expression (ASUITE11-4412,
// S3776). A 1:1 translation; alreadyExistMealBreak/isEntryTimeOffValid/
// isMealBreakOrNursing are precomputed by the caller (already pure, no need
// to re-derive them here) and passed in as booleans.
//
// The "could not edit for common user..." clause's
// "entryProjectWorkOrder.value !== A || !== B || !== C || !== D" is a
// tautology — a single value can differ from at most one of four distinct
// constants at a time, so this OR is always true. Preserved as-is
// (characterization, not a fix).
// ---------------------------------------------------------------------------

export type SaveDisabledEntryState = {
  isEntryValid: boolean;
  isEntryTouched: boolean;
  entryCanEdit: boolean;
  entryHours: any;
  entryProjectWorkOrder: SelectItemNumber;
  entryDate: string | Date;
};

export type SaveDisabledContext = {
  partialInactiveEntryProjectWorkOrder: any;
  editMode: boolean;
  isHolidayFromBamboo: boolean;
  maxHoursAllowed: number;
  alreadyExistsMealBreak: boolean;
  isEntryTimeOffValid: boolean;
  isSuperTimeAdmin: boolean;
  isMealBreakOrNursing: boolean;
  previousWorkableDay: Date;
  today: Date;
  activeUserId: any;
  userIdParam: any;
  lastWorkableDay: Date;
  isAnyOfSupervisorPoDelivery: boolean;
  isImportedFromChangeGearOrBamboo: boolean;
  disabledByTimeViewer: boolean;
};

export const computeSaveDisabled = (
  entryState: SaveDisabledEntryState,
  context: SaveDisabledContext,
): boolean =>
  !entryState.isEntryValid ||
  !entryState.isEntryTouched ||
  // ASUITE11-2512: can't edit on inactive proyect/wo/task
  context.partialInactiveEntryProjectWorkOrder ||
  (context.editMode &&
    (context.isHolidayFromBamboo
      ? context.maxHoursAllowed < Number(entryState.entryHours)
      : !entryState.entryCanEdit)) ||
  (!context.editMode && context.alreadyExistsMealBreak) ||
  !context.isEntryTimeOffValid ||
  // unpaid time off (meal break and nursing) for supervisor/project owner
  (!context.isSuperTimeAdmin &&
    context.isMealBreakOrNursing &&
    !isBetweenTwoDates(
      entryState.entryDate as string,
      context.previousWorkableDay,
      context.today,
    )) ||
  // could not edit for common user or supervisor/project owner for himself outside current week
  (!context.isSuperTimeAdmin &&
    context.activeUserId === context.userIdParam &&
    (entryState.entryProjectWorkOrder.value !== PROJ_MEAL_BREAK_ID ||
      entryState.entryProjectWorkOrder.value !== PROJ_NURSING_MOTHER_ID ||
      entryState.entryProjectWorkOrder.value !== PROJ_PAID_TIME_OFF_ID ||
      entryState.entryProjectWorkOrder.value !== PROJ_UNPAID_TIME_OFF_ID) &&
    !isBetweenTwoDates(
      entryState.entryDate as string,
      null,
      context.lastWorkableDay,
    )) ||
  // ASUITE11-2507: meal break or nursing older than today for superadmin/supervisor/PO/delivery for other
  (!isBetweenTwoDates(entryState.entryDate as string, null, context.today) &&
    context.isMealBreakOrNursing &&
    ((context.isSuperTimeAdmin &&
      context.activeUserId !== context.userIdParam) ||
      (context.isAnyOfSupervisorPoDelivery &&
        context.activeUserId !== context.userIdParam))) ||
  // ASUITE11-2561
  context.isImportedFromChangeGearOrBamboo ||
  // ASUITE11-2418: Time Viewer Role - FE
  context.disabledByTimeViewer;

// ---------------------------------------------------------------------------
// loadPropsState (ASUITE11-4412, S3776), split into its three separable
// concerns: entryDateValid's own nested ternary, the base state's
// project-work-order/task-type/non-billable-reason fallback chains, and the
// four "set time entry initial state when is X" if-blocks layered on top.
// Each 1:1 translations of the original logic.
// ---------------------------------------------------------------------------

export type LoadPropsStateContext = {
  isSuperTimeAdmin: boolean;
  isAnyOfSupervisorPoDelivery: boolean;
  activeUserId: any;
  userIdParam: any;
  editMode: boolean;
  isModal: boolean;
  previousWorkableDay: Date;
  lastWorkableDay: Date;
  partialInactiveEntryProjectWorkOrder: any;
  taskTypes: any[];
  internalNonBillableReason: any;
  benefitsTimeOffNonBillableReason: any;
  unpaidTimeOffNonBillableReason: any;
  defaultTaskTypeNursingMother: any;
};

export const computeLoadPropsEntryDateValid = (
  incomingState: { entryDateValid: boolean; entryDate: string | Date },
  context: Pick<
    LoadPropsStateContext,
    | "isSuperTimeAdmin"
    | "isAnyOfSupervisorPoDelivery"
    | "activeUserId"
    | "userIdParam"
    | "editMode"
    | "previousWorkableDay"
    | "lastWorkableDay"
  >,
): boolean =>
  incomingState.entryDateValid &&
  (context.isSuperTimeAdmin ||
    (context.isAnyOfSupervisorPoDelivery &&
      context.activeUserId !== context.userIdParam) ||
    (context.editMode
      ? new Date(incomingState.entryDate) < context.lastWorkableDay
      : context.previousWorkableDay <= new Date(incomingState.entryDate) &&
        new Date(incomingState.entryDate) < context.lastWorkableDay));

export const computeLoadPropsBaseState = (
  incomingState: any,
  entryDateValid: boolean,
  context: Pick<
    LoadPropsStateContext,
    | "partialInactiveEntryProjectWorkOrder"
    | "taskTypes"
    | "internalNonBillableReason"
  >,
): TimeEntryForm => ({
  entryCanEdit: incomingState.entryCanEdit,
  entryCanDelete: incomingState.entryCanDelete,
  entryId: incomingState.entryId,
  entryDate: incomingState.entryDate,
  entryDateValid,
  entryProjectWorkOrder: incomingState.entryProjectWorkOrder
    ? incomingState.entryProjectWorkOrder
    : context.partialInactiveEntryProjectWorkOrder
      ? context.partialInactiveEntryProjectWorkOrder
      : emptyProjectWorkOrder,
  entryProjectWorkOrderValid: incomingState.entryProjectWorkOrder
    ? incomingState.entryProjectWorkOrderValid
    : false,
  entryHours: incomingState.entryHours,
  entryHoursValid: incomingState.entryHoursValid,
  // if projectWorkOrder has default task type
  entryTaskType: incomingState.entryTaskType
    ? incomingState.entryTaskType
    : incomingState.entryProjectWorkOrder?.defaultTaskTypeId
      ? getValueFromSelect(
          context.taskTypes,
          incomingState.entryProjectWorkOrder.defaultTaskTypeId,
        )
      : empty,
  entryTaskTypeValid: incomingState.entryTaskTypeValid,
  entryDescription: incomingState.entryDescription,
  entryIsBillable: incomingState.entryIsBillable,
  // on edition, logic to disabled billable checkbox and set task type internal
  entryNonBillableReason:
    incomingState.entryProjectWorkOrder?.isInternal &&
    incomingState.entryProjectWorkOrder?.entityId !== 24056
      ? context.internalNonBillableReason
      : incomingState.entryNonBillableReason,
  entryNonBillableReasonValid: incomingState.entryNonBillableReasonValid,
  isEntryValid: incomingState.isEntryValid,
  isEntryTouched: incomingState.isEntryTouched,
});

// ASUITE11-4412, S3776 follow-up: a local Sonar run after the original
// extraction still flagged this function (complexity 20) — the meal-break
// branch's nested if + three ternaries + a further-nested if were still
// counted here even though they'd moved out of loadPropsState. Pulling that
// branch into its own function (with an early return replacing the
// wrapping if) removes the nesting bonus that was driving the score.
const applyMealBreakOverrides = (
  result: TimeEntryForm,
  incomingState: any,
  context: Pick<LoadPropsStateContext, "editMode" | "isModal">,
): void => {
  result.entryTaskTypeValid = true;
  result.entryNonBillableReasonValid = true;
  result.isEntryValid = true;
  if (context.editMode) return;

  result.entryDescription = incomingState.entryDescription
    ? incomingState.entryDescription
    : "12:00pm to 12:30pm";
  result.entryHours = incomingState.entryHours
    ? incomingState.entryHours
    : 0.5;
  result.entryHoursValid = incomingState.entryHoursValid
    ? incomingState.entryHoursValid
    : true;
  if (context.isModal) {
    result.isEntryTouched = true;
  }
};

export const applyProjectWorkOrderTypeOverrides = (
  newState: TimeEntryForm,
  incomingState: any,
  context: Pick<
    LoadPropsStateContext,
    | "editMode"
    | "isModal"
    | "benefitsTimeOffNonBillableReason"
    | "unpaidTimeOffNonBillableReason"
    | "defaultTaskTypeNursingMother"
  >,
): TimeEntryForm => {
  const result: TimeEntryForm = { ...newState };

  // set time entry initial state when is nursing mother
  if (incomingState.entryProjectWorkOrder?.value === PROJ_NURSING_MOTHER_ID) {
    result.entryIsBillable = false;
    result.entryNonBillableReason = context.benefitsTimeOffNonBillableReason;
    result.entryNonBillableReasonValid = true;
    result.entryTaskType = context.defaultTaskTypeNursingMother;
    result.entryTaskTypeValid = true;
  }

  // set time entry initial state when is meal break
  if (incomingState.entryProjectWorkOrder?.value === PROJ_MEAL_BREAK_ID) {
    applyMealBreakOverrides(result, incomingState, context);
  }

  // set time entry initial state when is Paid time off
  if (incomingState.entryProjectWorkOrder?.value === PROJ_PAID_TIME_OFF_ID) {
    result.entryIsBillable = false;
    result.entryNonBillableReason = context.benefitsTimeOffNonBillableReason;
    result.entryNonBillableReasonValid = true;
  }

  // set time entry initial state when is Unpaid time off
  if (incomingState.entryProjectWorkOrder?.value === PROJ_UNPAID_TIME_OFF_ID) {
    result.entryIsBillable = false;
    result.entryNonBillableReasonValid = true;
    result.entryNonBillableReason =
      result.entryTaskType.value === 31
        ? context.benefitsTimeOffNonBillableReason
        : context.unpaidTimeOffNonBillableReason;
  }

  return result;
};
